import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import CustomerModal from './CustomerModal';
import { customerService } from '../services/customerService';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const sortedCustomers = [...customers].sort((a, b) =>
    sortDir === 'asc'
      ? a.name.localeCompare(b.name)
      : b.name.localeCompare(a.name)
  );

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await customerService.getCustomers();
      setCustomers(data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAddCustomer = async (formData) => {
    await customerService.createCustomer(formData);
    await fetchCustomers();
    setShowModal(false);
    showSuccess('Customer added successfully!');
  };

  const handleEditCustomer = async (formData) => {
    await customerService.updateCustomer(editingCustomer.id, formData);
    await fetchCustomers();
    setEditingCustomer(null);
    setShowModal(false);
    showSuccess('Customer updated successfully!');
  };

  const handleDeleteCustomer = async (id) => {
    try {
      await customerService.deleteCustomer(id);
      await fetchCustomers();
      setDeleteConfirm(null);
      showSuccess('Customer deleted successfully!');
    } catch (err) {
      setError('Failed to delete customer. Please try again.');
    }
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Customers</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">
                {user?.name}
              </span>
              <button
                onClick={logout}
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-8">

        {/* Success message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-700 dark:text-green-300 text-sm font-medium">
            ✓ {successMessage}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Page title row with Add button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Customer List</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {customers.length} {customers.length === 1 ? 'customer' : 'customers'} total
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md"
          >
            <span className="text-lg leading-none">+</span>
            Add Customer
          </button>
        </div>

        {/* Customers table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                Loading customers...
              </div>
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-5xl mb-4">👥</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No customers yet</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">Add your first customer to get started.</p>
              <button
                onClick={openAddModal}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <span className="text-lg leading-none">+</span>
                Add Customer
              </button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th
                        className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                      >
                        Name {sortDir === 'asc' ? '↑' : '↓'}
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Street Address</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Town</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">County</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Country</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Postcode</th>
                      <th className="sticky right-0 text-right px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]" style={{ backgroundColor: '#2b3544' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedCustomers.map((customer) => (
                      <tr
                        key={customer.id}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                            {customer.name}
                          </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{customer.street_address || '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{customer.town || '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{customer.county || '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{customer.country}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {customer.postcode
                            ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([customer.name, customer.street_address, customer.town, customer.county, customer.postcode].filter(Boolean).join(', '))}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600 dark:text-blue-400">{customer.postcode}</a>
                            : '—'}
                        </td>
                        <td className="sticky right-0 bg-white dark:bg-gray-800 px-6 py-4 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(customer)}
                              className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(customer)}
                              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {customers.map((customer) => (
                  <div key={customer.id} className="p-5 space-y-2">
                    <div className="flex justify-between items-start">
                      <button
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        className="font-semibold text-blue-600 dark:text-blue-400 hover:underline text-left"
                      >
                        {customer.name}
                      </button>
                      <div className="flex gap-2 ml-2">
                        <button
                          onClick={() => openEditModal(customer)}
                          className="text-sm text-blue-600 dark:text-blue-400 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(customer)}
                          className="text-sm text-red-600 dark:text-red-400 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{customer.street_address}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {[customer.town, customer.county].filter(Boolean).join(', ')}
                      {customer.postcode && <>{[customer.town, customer.county].filter(Boolean).length > 0 ? ', ' : ''}<a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([customer.name, customer.street_address, customer.town, customer.county, customer.postcode].filter(Boolean).join(', '))}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-600 dark:text-blue-400">{customer.postcode}</a></>}
                      {customer.country && <>{([customer.town, customer.county].filter(Boolean).length > 0 || customer.postcode) ? ', ' : ''}{customer.country}</>}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <CustomerModal
          customer={editingCustomer}
          onSave={editingCustomer ? handleEditCustomer : handleAddCustomer}
          onClose={closeModal}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Customer</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCustomer(deleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
